import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import { FileCheck2, Loader2, Printer, ReceiptText, Truck, Wallet } from 'lucide-react'
import { useRef, useState } from 'react'

import { Invoice } from '@/components/Invoice'
import { KpiCard } from '@/components/KpiCard'
import { ScreenHeader } from '@/components/ScreenHeader'
import { StatusBadge } from '@/components/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Comanda, EFacturaStatus, Factura } from '@/db/dexie'
import { genereazaFactura, getClienti, getComenzi, getFactura, getFacturi } from '@/db/queries'
import { fmtData, fmtKg, fmtRON, fmtSuma } from '@/lib/format'

function EFacturaBadge({ status }: { status: EFacturaStatus }) {
  if (status === 'validat') return <Badge variant="success">Validat ✓</Badge>
  if (status === 'trimis') return <Badge variant="success">Trimis ✓</Badge>
  return <Badge variant="warning">În așteptare</Badge>
}

export default function Facturare() {
  const [preview, setPreview] = useState<Factura | null>(null)
  const [genId, setGenId] = useState<string | null>(null)

  const data = useLiveQuery(async () => {
    const [comenzi, facturi, clienti] = await Promise.all([
      getComenzi(),
      getFacturi(),
      getClienti(),
    ])
    const numeClient = new Map(clienti.map((c) => [c.id, c.nume]))
    const comandaById = new Map(comenzi.map((c) => [c.id, c]))
    const deFacturat = comenzi
      .filter((c) => c.status === 'Livrat' && !c.facturaId)
      .sort((a, b) => (a.dataLivrare < b.dataLivrare ? 1 : -1))
    const valoareFacturat = facturi.reduce((s, f) => s + f.total, 0)
    return { comenzi, facturi, deFacturat, numeClient, comandaById, valoareFacturat }
  }, [])

  async function handleGenereaza(cmd: Comanda) {
    setGenId(cmd.id)
    try {
      const id = await genereazaFactura(cmd.id)
      const factura = id ? await getFactura(id) : undefined
      if (factura) setPreview(factura)
    } finally {
      setGenId(null)
    }
  }

  const facturi = data?.facturi ?? []
  const deFacturat = data?.deFacturat ?? []
  // Retain the last-previewed invoice so the dialog's close animation never
  // strands an empty shell — content stays until the modal is fully unmounted.
  const lastPreview = useRef<Factura | null>(null)
  if (preview) lastPreview.current = preview
  const shown = preview ?? lastPreview.current
  const shownComanda = shown ? data?.comandaById.get(shown.comandaId) : undefined
  const gol = data && facturi.length === 0 && deFacturat.length === 0

  return (
    <div>
      <ScreenHeader tab="facturare" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Facturi emise"
          value={facturi.length}
          icon={ReceiptText}
          hint="serie EXS"
          index={0}
        />
        <KpiCard
          label="Valoare facturată"
          value={data?.valoareFacturat ?? 0}
          format={fmtRON}
          icon={Wallet}
          hint="TVA 9% inclus"
          index={1}
        />
        <KpiCard
          label="De facturat"
          value={deFacturat.length}
          icon={FileCheck2}
          delta={deFacturat.length ? 'livrări nefacturate' : undefined}
          deltaTone="tomato"
          hint={deFacturat.length ? undefined : 'totul la zi'}
          index={2}
        />
      </div>

      {gol && (
        <div className="mt-8 grid place-items-center rounded-xl border border-dashed border-border bg-card/50 px-6 py-16 text-center">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-amber-soft text-amber-ink shadow-paper">
            <ReceiptText className="size-6" />
          </div>
          <p className="mt-4 font-display text-lg text-foreground">Nicio factură încă</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Facturile apar aici după ce o comandă este livrată și facturată.
          </p>
        </div>
      )}

      {deFacturat.length > 0 && (
        <section className="mt-8 space-y-3">
          <div>
            <h2 className="font-display text-lg font-medium">De facturat</h2>
            <p className="text-sm text-muted-foreground">
              Comenzi livrate care așteaptă factura. TVA 9% (aliment) se calculează automat, iar
              factura pleacă la ANAF prin e-Factura.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {deFacturat.map((cmd, i) => (
              <motion.div
                key={cmd.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-paper sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">
                      {data?.numeClient.get(cmd.clientId) ?? cmd.clientId}
                    </span>
                    <StatusBadge status={cmd.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-mono nums">{cmd.id}</span>
                    <span>{fmtKg(cmd.totalKg)}</span>
                    <span className="font-mono nums text-foreground">{fmtRON(cmd.totalRON)}</span>
                    <span>livrat {fmtData(cmd.dataLivrare)}</span>
                  </div>
                  {cmd.codUIT && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Truck className="size-3" /> cod UIT{' '}
                      <span className="font-mono nums">{cmd.codUIT}</span>
                    </span>
                  )}
                </div>
                <Button
                  variant="pine"
                  size="sm"
                  onClick={() => handleGenereaza(cmd)}
                  disabled={genId === cmd.id}
                  className="shrink-0"
                >
                  {genId === cmd.id ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ReceiptText className="size-4" />
                  )}
                  Generează factură
                </Button>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {facturi.length > 0 && (
        <section className="mt-8 space-y-3">
          <h2 className="font-display text-lg font-medium">Facturi emise</h2>
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-paper">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serie / Nr.</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Bază</TableHead>
                  <TableHead className="text-right">TVA 9%</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>e-Factura</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {facturi.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-mono nums font-medium">
                      {f.serie} {f.numar}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtData(f.dataEmitere)}
                    </TableCell>
                    <TableCell className="max-w-[14rem] truncate">{f.client.nume}</TableCell>
                    <TableCell className="text-right font-mono nums">
                      {fmtSuma(f.subtotal)}
                    </TableCell>
                    <TableCell className="text-right font-mono nums">
                      {fmtSuma(f.totalTVA)}
                    </TableCell>
                    <TableCell className="text-right font-mono nums font-medium text-amber-ink">
                      {fmtSuma(f.total)}
                    </TableCell>
                    <TableCell>
                      <EFacturaBadge status={f.eFacturaStatus} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setPreview(f)}>
                        <Printer className="size-4" /> Vezi / Printează
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      <Dialog
        open={!!preview}
        onOpenChange={(o) => {
          if (!o) setPreview(null)
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Factură {shown ? `${shown.serie} ${shown.numar}` : ''}</DialogTitle>
            <DialogDescription>
              Previzualizare document fiscal · pregătit pentru tipărire și e-Factura.
            </DialogDescription>
          </DialogHeader>
          {shown && <Invoice factura={shown} comanda={shownComanda} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}
